import * as XLSX from "xlsx";

// Builds the inputs for the existing bulk override path (the same one EditItem.jsx's Excel upload
// uses): a CSV at Boffin/uploads/pending/Override_<computeId>.csv plus a contico trigger JSON that
// runs the backend LoadOverrideFile plugin. LoadOverrideFile then does, in ONE Lambda run:
//   DELETE staging rows -> LOAD DATA FROM S3 (this CSV) into compute_data_override
//   -> one UPDATE compute_data JOIN compute_data_override ON RowId -> ReComputeNew.
// That replaces one /executeOnDemand call per changed row and guarantees every override is written
// before the recompute reads it.

// LOAD DATA maps CSV fields to table columns BY POSITION (the header line is skipped), so the CSV
// must have exactly these columns in exactly this order. This is the header of the compute data
// file the grid loads (Boffin/compute/data/data_<computeId>.csv, written by the backend
// PrepareXLFile from the same query as the downloadable Excel), which matches the backend
// BoffinConstants.override_load_columns position-for-position. Position 17 is named
// "transaction_currency" in the data file and loads into staging's "functional_currency" column.
// Do NOT reorder, add, or remove entries without a matching backend change.
export const OVERRIDE_UPLOAD_COLUMNS = [
  "RowId",
  "id",
  "dataset",
  "ledger_id",
  "ledger_name",
  "periodname",
  "reference_id",
  "company",
  "location",
  "costcenter",
  "account",
  "product",
  "channel",
  "project",
  "debit",
  "credit",
  "transaction_currency",
  "journalname",
  "linedescription",
  "suppliernumber",
  "suppliername",
  "dateplacedinservice",
  "ponumber",
  "invoicenumber",
  "userid",
  "book_type_code",
  "po_item_category",
  "islocation_cip",
  "location_go_live_date",
  "isproject_cip",
  "project_go_live_date",
  "final_date_for_comparison",
  "isproject_lhi",
  "is_building",
  "lease_end_date",
  "sl_maj_cat",
  "sl_min_cat",
  "sl_book",
  "final_maj_cat",
  "final_min_cat",
  "eul",
  "dep_in_months",
  "dep_amount",
  "cost_acct",
  "acc_dep_acct",
  "dep_acct",
  "WarningMsg",
  "Rule_Number",
  "Override",
  "Override_Prepare_JE_Reclass",
  "Override_Prepare_JE_Dep_Accrual",
  "Override_CIP_YN",
  "Override_InServiceDate_Month",
  "Override_InServiceDate_Year",
  "Override_Major_Min_Category",
  "Override_EUL",
  "Validation_Status",
  "Validation_err_msg",
  "JE_POSTED_STATUS",
  "Suggested_Maj_Cat",
  "Suggested_Min_Cat",
  "Logic_Checks",
  "last_update_date",
  "Suggested_sl_book",
  "teamname",
];

export const overrideUploadPath = (computeId) =>
  `public/Boffin/uploads/pending/Override_${computeId}.csv`;

export const overrideTriggerPath = (computeId) =>
  `public/contico_lambda_trigger/pending/${computeId}.json`;

export const overrideTriggerBody = (computeId) =>
  JSON.stringify({
    processName: "boffin",
    scriptName: "LoadOverrideFile",
    parameters: { computeId },
  });

// One entry per RowId (last occurrence kept). Re-applying the same row on screen appends it to
// appliedChanges again; today every copy is the same mutated grid row object, so the copies are
// identical and this only drops redundant CSV lines. It also guards against a future change that
// copies rows on apply: duplicate RowIds with DIFFERENT values in the staging table would make the
// backend's joined UPDATE pick one of them arbitrarily.
export const dedupeByRowId = (rows) => {
  const byRowId = new Map();
  (rows || []).forEach((row) => {
    if (row && row.RowId !== undefined && row.RowId !== null && row.RowId !== "") {
      byRowId.set(String(row.RowId), row);
    }
  });
  return Array.from(byRowId.values());
};

// RowIds that appear more than once in `rows`, in first-seen order. RowIds are compared as trimmed
// strings, so 41813552, "41813552" and " 41813552 " are the same row. Rows without a RowId are ignored.
// Used by the Excel override upload: a user-edited file with the same RowId twice is ambiguous (the
// backend's joined UPDATE would apply one of the copies arbitrarily), so the upload is rejected.
export const findDuplicateRowIds = (rows) => {
  const counts = new Map();
  (rows || []).forEach((row) => {
    const raw = row ? row.RowId : undefined;
    if (raw === undefined || raw === null) return;
    const key = String(raw).trim();
    if (key === "") return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([rowId]) => rowId);
};

// Rows whose "id" (column B of the downloaded override file: the compute id each row belongs to)
// is not `computeId`. Compared as trimmed strings; a blank/missing id counts as a mismatch.
// Used by the Excel override upload: the backend LoadOverrideFile only applies staging rows
// WHERE b.id = '<computeId>' AND a.id = b.id, so rows from another compute's file (or with the id
// cleared) would be silently skipped while the upload still reports success.
// Returns { rowCount, foundIds }: how many rows mismatch, and the distinct ids they carry in
// first-seen order ("" for blank).
export const findComputeIdMismatches = (rows, computeId) => {
  const expected = String(computeId === undefined || computeId === null ? "" : computeId).trim();
  const foundIds = [];
  let rowCount = 0;
  (rows || []).forEach((row) => {
    if (!row) return;
    const raw = row.id;
    const value = raw === undefined || raw === null ? "" : String(raw).trim();
    if (expected !== "" && value === expected) return;
    rowCount += 1;
    if (!foundIds.includes(value)) foundIds.push(value);
  });
  return { rowCount, foundIds };
};

// Project each row onto OVERRIDE_UPLOAD_COLUMNS (missing values -> "") so the CSV's columns can't
// drift with whatever keys the row objects happen to carry. Override is forced to "Yes": the
// backend only applies staging rows WHERE Override = 'Yes', so a changed row without it would be
// silently skipped (the old per-row SQL set Override = 'Yes' itself).
export const buildOverrideUploadCsv = (appliedChanges) => {
  const rows = dedupeByRowId(appliedChanges).map((row) => {
    const out = {};
    OVERRIDE_UPLOAD_COLUMNS.forEach((col) => {
      const value = row[col];
      out[col] = value === undefined || value === null ? "" : value;
    });
    out.Override = "Yes";
    return out;
  });
  const sheet = XLSX.utils.json_to_sheet(rows, { header: OVERRIDE_UPLOAD_COLUMNS });
  return { csv: XLSX.utils.sheet_to_csv(sheet), rowCount: rows.length };
};
